import moment, { Moment } from 'moment';
import {
  DEFAULT_PACKAGE_NIGHTS,
  MAX_PACKAGE_NIGHTS,
  MIN_PACKAGE_NIGHTS,
} from '../../../utils/constants';

// Events API returns dateOfEvent as "DD/MM/YYYY" regardless of siteConfig.dateFormat
export const EVENT_DATE_FORMAT = 'DD/MM/YYYY';

export const parseEventDate = (
  dateOfEvent?: string | null
): moment.Moment | null => {
  if (!dateOfEvent) return null;
  const eventDate = moment(dateOfEvent, EVENT_DATE_FORMAT, true);
  return eventDate.isValid() ? eventDate : null;
};

/** Nights between a check-in/check-out pair, or null when either is missing. */
export const nightsBetween = (
  from?: Moment | null,
  to?: Moment | null
): number | null => {
  if (!from?.isValid() || !to?.isValid()) return null;
  const nights = to.diff(from, 'days');
  return nights > 0 ? nights : null;
};

/**
 * Package (hotel+flight) range for a stay of `nights` nights around an event:
 * check-in is `ceil(nights / 2)` days before the event (so odd stays lean
 * towards the nights *before* it) and check-out is `nights` later, which keeps
 * the event date inside the range.
 *
 * A check-in before today slides the whole window forward instead of shrinking
 * it — the stay length is user-chosen on the Nights step, so it is preserved.
 *
 * Returns null when dateOfEvent is missing/unparsable, or when the event is
 * already in the past (no range could contain it).
 */
export const computePackageDatesForNights = (
  dateOfEvent: string | null | undefined,
  nights: number,
  dateFormat: string
): { from: string; to: string } | null => {
  const eventDate = parseEventDate(dateOfEvent);
  if (!eventDate) return null;

  const today = moment().startOf('day');
  if (eventDate.isBefore(today)) return null;

  const stay = Math.min(
    Math.max(Math.round(nights) || DEFAULT_PACKAGE_NIGHTS, MIN_PACKAGE_NIGHTS),
    MAX_PACKAGE_NIGHTS
  );

  let start = eventDate.clone().subtract(Math.ceil(stay / 2), 'days');
  if (start.isBefore(today)) {
    start = today;
  }
  const end = start.clone().add(stay, 'days');

  return {
    from: start.format(dateFormat),
    to: end.format(dateFormat),
  };
};
